/**
 * Conversión "unidad de receta" → "unidad de súper".
 *
 * Problema: el plan alimenticio dice "210 g de aguacate" o "3½ taza de arroz".
 * Cuando vas al súper esa información no es útil: ¿cuántos aguacates?
 * ¿una bolsa o dos de arroz?
 *
 * Este módulo traduce de las unidades de la receta a la unidad COMERCIAL
 * de México (piezas, latas, bolsas, botes, paquetes). Si un ingrediente
 * no está catalogado, la UI cae al formato original.
 *
 * BUFFER: para frutas/verduras frescas vendidas por pieza, agregamos 1
 * pieza extra al cálculo (pueden venir chicas, perderse alguna). Para
 * enlatados/empaquetados/granos, no — su tamaño de envase ya da margen.
 *
 * MULTIPLICADOR (personas × días): la UI lo aplica a `qty` ANTES de
 * llamar a `recommendPurchase`. Este módulo no sabe de personas/días.
 */

export interface PurchaseRecommendation {
  /** Cantidad redondeada a comprar (ej. 5, 2, 1). */
  count: number;
  /** Etiqueta legible en el contexto del súper.
   *  Ej: "5 latas de 130g", "2 piezas", "1 bolsa de 1 kg". */
  label: string;
  /** Detalle adicional opcional, ej. "cubre con sobrante" o "≈840g". */
  hint?: string;
  /** Emoji opcional para reforzar visualmente. */
  emoji?: string;
}

export interface ConvertOpts {
  /** Piezas extra que sumar al resultado para frutas/verduras frescas.
   *  La capa de UI lo pasa (típicamente 1). Si la entry NO es fresh
   *  produce, el buffer se ignora. */
  bufferPieces?: number;
}

interface IngredientEntry {
  /** Variantes del nombre que matchean este ingrediente (case-insensitive,
   *  sin acentos). El catálogo se busca por substring. */
  aliases: string[];
  /** Emoji opcional. */
  emoji?: string;
  /** Marca que ESTE ingrediente se vende por pieza fresca (aguacate,
   *  manzana, jitomate). Habilita el `bufferPieces` extra. */
  isFreshProduce?: boolean;
  /** Convierte la cantidad de la receta a recomendación de compra.
   *  qty/unit son lo que ya agregó el builder × multiplier de UI.
   *  Si devuelve null, la UI usa el formato original. */
  convert: (qty: number, unit: string, opts?: ConvertOpts) => PurchaseRecommendation | null;
}

/** Strip acentos + lowercase para hacer match robusto. */
function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();
}

/** Redondea hacia arriba (siempre comprar de más, nunca de menos). */
const ceil = (n: number) => Math.max(1, Math.ceil(n));

/** Convierte cantidad+unidad a gramos cuando se puede.
 *  Solo unidades de masa básicas. Devuelve null si la unidad no es masa. */
function toGrams(qty: number, unit: string): number | null {
  const u = normalize(unit);
  if (u === 'g' || u === 'gr' || u === 'gramo' || u === 'gramos') return qty;
  if (u === 'kg' || u === 'kilo' || u === 'kilos') return qty * 1000;
  return null;
}

/** Convierte cantidad+unidad a mililitros cuando se puede. */
function toMl(qty: number, unit: string): number | null {
  const u = normalize(unit);
  if (u === 'ml') return qty;
  if (u === 'l' || u === 'lt' || u === 'litro' || u === 'litros') return qty * 1000;
  return null;
}

/** Acepta tza / tzas / taza / tazas (con o sin acentos). El plan a veces
 *  guarda 'tza' (abreviado) y otras 'taza' (palabra completa). */
function isTaza(unit: string): boolean {
  const u = normalize(unit);
  return u === 'tza' || u === 'tzas' || u.startsWith('taza');
}

/** Acepta pza / pzas / pieza / piezas. */
function isPieza(unit: string): boolean {
  const u = normalize(unit);
  return u === 'pza' || u === 'pzas' || u.startsWith('pieza');
}

/** Helper: produce un label tipo "N nombre(s)" con plural correcto. */
function piecesLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** Catálogo. Orden = prioridad de match (los más específicos arriba). */
const INGREDIENT_CATALOG: IngredientEntry[] = [
  // ─── Proteínas ───
  {
    aliases: ['huevo', 'huevos completos', 'huevos enteros'],
    emoji: '🥚',
    convert: (qty, unit) => {
      if (!isPieza(unit)) return null;
      const eggs = ceil(qty);
      if (eggs <= 12) return { count: eggs, label: piecesLabel(eggs, 'huevo', 'huevos') };
      if (eggs <= 18) return { count: 1, label: '1 cartón de 18 huevos' };
      return { count: ceil(eggs / 18), label: `${ceil(eggs / 18)} cartones de 18 huevos` };
    },
  },
  {
    aliases: ['claras', 'clara de huevo', 'claras de huevo'],
    emoji: '🥚',
    convert: (qty, unit) => {
      if (!isPieza(unit)) return null;
      const cl = ceil(qty);
      if (cl <= 16) return { count: 1, label: '1 bote de claras (500ml)' };
      return { count: ceil(cl / 16), label: `${ceil(cl / 16)} botes de claras (500ml c/u)` };
    },
  },
  {
    aliases: ['atun', 'lata de atun'],
    emoji: '🐟',
    convert: (qty, unit) => {
      const g = toGrams(qty, unit);
      if (g == null) return null;
      const cans = ceil(g / 130);
      return { count: cans, label: `${cans} ${cans === 1 ? 'lata' : 'latas'} de atún (130g c/u)` };
    },
  },
  {
    aliases: ['pechuga', 'pollo', 'pechuga de pollo'],
    emoji: '🍗',
    convert: (qty, unit) => {
      const g = toGrams(qty, unit);
      if (g == null) return null;
      if (g >= 1000) {
        const kg = (g / 1000).toFixed(g % 1000 === 0 ? 0 : 1);
        return { count: 1, label: `${kg} kg de pechuga` };
      }
      return { count: 1, label: `${Math.round(g)} g de pechuga`, hint: 'pide al carnicero' };
    },
  },
  {
    aliases: ['res', 'bola de res', 'carne de res', 'molida de res', 'carne molida'],
    emoji: '🥩',
    convert: (qty, unit) => {
      const g = toGrams(qty, unit);
      if (g == null) return null;
      if (g >= 1000) {
        const kg = (g / 1000).toFixed(g % 1000 === 0 ? 0 : 1);
        return { count: 1, label: `${kg} kg de res` };
      }
      return { count: 1, label: `${Math.round(g)} g de res`, hint: 'pide al carnicero' };
    },
  },
  {
    aliases: ['pescado', 'filete de pescado', 'tilapia', 'salmon', 'mojarra'],
    emoji: '🐟',
    convert: (qty, unit) => {
      const g = toGrams(qty, unit);
      if (g == null) return null;
      const fillets = ceil(g / 150);
      return { count: fillets, label: `${fillets} ${fillets === 1 ? 'filete' : 'filetes'} (≈150g c/u)` };
    },
  },

  // ─── Frutas (vendidas por pieza, fresh produce con buffer) ───
  {
    aliases: ['aguacate'],
    emoji: '🥑',
    isFreshProduce: true,
    convert: (qty, unit, opts) => {
      const buf = opts?.bufferPieces ?? 0;
      const g = toGrams(qty, unit);
      if (g != null) {
        const pcs = ceil(g / 200) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'aguacate', 'aguacates') };
      }
      if (isPieza(unit)) {
        const pcs = ceil(qty) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'aguacate', 'aguacates') };
      }
      return null;
    },
  },
  {
    aliases: ['manzana'],
    emoji: '🍎',
    isFreshProduce: true,
    convert: (qty, unit, opts) => {
      const buf = opts?.bufferPieces ?? 0;
      if (isPieza(unit)) {
        const pcs = ceil(qty) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'manzana', 'manzanas') };
      }
      const g = toGrams(qty, unit);
      if (g != null) {
        const pcs = ceil(g / 180) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'manzana', 'manzanas') };
      }
      return null;
    },
  },
  {
    aliases: ['platano', 'banana'],
    emoji: '🍌',
    isFreshProduce: true,
    convert: (qty, unit, opts) => {
      const buf = opts?.bufferPieces ?? 0;
      if (isPieza(unit)) {
        const pcs = ceil(qty) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'plátano', 'plátanos') };
      }
      const g = toGrams(qty, unit);
      if (g != null) {
        const pcs = ceil(g / 120) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'plátano', 'plátanos') };
      }
      return null;
    },
  },
  {
    aliases: ['naranja'],
    emoji: '🍊',
    isFreshProduce: true,
    convert: (qty, unit, opts) => {
      const buf = opts?.bufferPieces ?? 0;
      if (isPieza(unit)) {
        const pcs = ceil(qty) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'naranja', 'naranjas') };
      }
      return null;
    },
  },
  {
    aliases: ['fresa', 'fresas'],
    emoji: '🍓',
    convert: (qty, unit) => {
      const g = toGrams(qty, unit);
      if (g == null) return null;
      const boxes = ceil(g / 500);
      return { count: boxes, label: `${boxes} ${boxes === 1 ? 'caja' : 'cajas'} de fresas (500g c/u)` };
    },
  },
  {
    aliases: ['uvas', 'uva'],
    emoji: '🍇',
    convert: (qty, unit) => {
      if (isPieza(unit)) {
        const grapes = ceil(qty);
        if (grapes <= 30) return { count: 1, label: '1 racimo chico de uvas' };
        if (grapes <= 60) return { count: 1, label: '1 racimo mediano de uvas' };
        return { count: 1, label: '1 racimo grande de uvas' };
      }
      const g = toGrams(qty, unit);
      if (g == null) return null;
      if (g <= 300) return { count: 1, label: '1 racimo chico de uvas' };
      if (g <= 600) return { count: 1, label: '1 racimo mediano de uvas' };
      return { count: 1, label: `${Math.round(g)} g de uvas` };
    },
  },

  // ─── Verduras frescas (con buffer) ───
  {
    aliases: ['jitomate', 'tomate', 'jitomates', 'tomates'],
    emoji: '🍅',
    isFreshProduce: true,
    convert: (qty, unit, opts) => {
      const buf = opts?.bufferPieces ?? 0;
      if (isPieza(unit)) {
        const pcs = ceil(qty) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'jitomate', 'jitomates') };
      }
      const g = toGrams(qty, unit);
      if (g != null) {
        const pcs = ceil(g / 120) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'jitomate', 'jitomates') };
      }
      return null;
    },
  },
  {
    aliases: ['lechuga'],
    emoji: '🥬',
    convert: (qty, unit) => {
      if (isTaza(unit)) {
        return { count: 1, label: '1 lechuga mediana' };
      }
      const g = toGrams(qty, unit);
      if (g != null) {
        const heads = ceil(g / 300);
        return { count: heads, label: `${heads} ${heads === 1 ? 'lechuga' : 'lechugas'}` };
      }
      return null;
    },
  },
  {
    aliases: ['espinaca', 'espinacas'],
    emoji: '🥬',
    convert: (qty, unit) => {
      if (isTaza(unit) || normalize(unit) === 'al gusto') {
        return { count: 1, label: '1 bolsa de espinacas (250g)' };
      }
      const g = toGrams(qty, unit);
      if (g == null) return null;
      const bags = ceil(g / 250);
      return { count: bags, label: `${bags} ${bags === 1 ? 'bolsa' : 'bolsas'} de espinacas (250g c/u)` };
    },
  },
  {
    aliases: ['brocoli'],
    emoji: '🥦',
    convert: (qty, unit) => {
      const g = toGrams(qty, unit);
      if (g == null) return null;
      const heads = ceil(g / 400);
      return { count: heads, label: `${heads} ${heads === 1 ? 'brócoli' : 'brócolis'}` };
    },
  },
  {
    aliases: ['zanahoria', 'zanahorias'],
    emoji: '🥕',
    isFreshProduce: true,
    convert: (qty, unit, opts) => {
      const buf = opts?.bufferPieces ?? 0;
      if (isPieza(unit)) {
        const pcs = ceil(qty) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'zanahoria', 'zanahorias') };
      }
      const g = toGrams(qty, unit);
      if (g != null) {
        const pcs = ceil(g / 80) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'zanahoria', 'zanahorias') };
      }
      return null;
    },
  },
  {
    aliases: ['pepino', 'pepinos'],
    emoji: '🥒',
    isFreshProduce: true,
    convert: (qty, unit, opts) => {
      const buf = opts?.bufferPieces ?? 0;
      if (isPieza(unit)) {
        const pcs = ceil(qty) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'pepino', 'pepinos') };
      }
      const g = toGrams(qty, unit);
      if (g != null) {
        const pcs = ceil(g / 200) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'pepino', 'pepinos') };
      }
      return null;
    },
  },
  {
    aliases: ['cebolla'],
    emoji: '🧅',
    isFreshProduce: true,
    convert: (qty, unit, opts) => {
      const buf = opts?.bufferPieces ?? 0;
      if (isPieza(unit)) {
        const pcs = ceil(qty) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'cebolla', 'cebollas') };
      }
      const g = toGrams(qty, unit);
      if (g != null) {
        const pcs = ceil(g / 150) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'cebolla', 'cebollas') };
      }
      return null;
    },
  },
  {
    aliases: ['ajo'],
    emoji: '🧄',
    convert: () => ({ count: 1, label: '1 cabeza de ajo' }),
  },
  {
    aliases: ['limon', 'limones'],
    emoji: '🍋',
    isFreshProduce: true,
    convert: (qty, unit, opts) => {
      const buf = opts?.bufferPieces ?? 0;
      if (isPieza(unit)) {
        const pcs = ceil(qty) + buf;
        return { count: pcs, label: piecesLabel(pcs, 'limón', 'limones') };
      }
      return null;
    },
  },

  // ─── Granos / carbohidratos ───
  {
    aliases: ['arroz', 'arroz integral', 'arroz hervido', 'arroz blanco'],
    emoji: '🍚',
    convert: (qty, unit) => {
      if (isTaza(unit)) {
        const gramsCrudo = qty * 60;
        if (gramsCrudo >= 800) {
          const bolsas = ceil(gramsCrudo / 1000);
          return { count: bolsas, label: `${bolsas} ${bolsas === 1 ? 'bolsa' : 'bolsas'} de arroz (1 kg)` };
        }
        return { count: 1, label: '1 bolsa de arroz (1 kg)' };
      }
      const g = toGrams(qty, unit);
      if (g != null) {
        const bolsas = ceil(g / 1000);
        return { count: bolsas, label: `${bolsas} ${bolsas === 1 ? 'bolsa' : 'bolsas'} de arroz (1 kg)` };
      }
      return null;
    },
  },
  {
    aliases: ['tortilla de maiz', 'tortillas de maiz', 'tortilla', 'tortillas'],
    emoji: '🌮',
    convert: (qty, unit) => {
      if (isPieza(unit)) {
        const pcs = ceil(qty);
        if (pcs <= 25) return { count: 1, label: '½ kg de tortillas (~25 piezas)' };
        return { count: ceil(pcs / 50), label: `${ceil(pcs / 50)} kg de tortillas` };
      }
      return null;
    },
  },
  {
    aliases: ['avena'],
    emoji: '🌾',
    convert: () => ({ count: 1, label: '1 caja de avena (400g)' }),
  },
  {
    aliases: ['pan integral', 'pan'],
    emoji: '🍞',
    convert: () => ({ count: 1, label: '1 paquete de pan integral' }),
  },
  {
    aliases: ['tostada', 'tostadas', 'tostada sanissimo', 'sanissimo'],
    emoji: '🍪',
    convert: (qty, unit) => {
      if (isPieza(unit)) {
        return { count: 1, label: '1 paquete de tostadas Sanissimo' };
      }
      return null;
    },
  },

  // ─── Lácteos ───
  {
    aliases: ['yogurt', 'yogur', 'yogurt griego'],
    emoji: '🥛',
    convert: (qty, unit) => {
      const ml = toMl(qty, unit) ?? toGrams(qty, unit);
      if (ml == null) return null;
      const boats = ceil(ml / 1000);
      return { count: boats, label: `${boats} ${boats === 1 ? 'bote' : 'botes'} de yogurt (1 kg c/u)` };
    },
  },
  {
    aliases: ['leche'],
    emoji: '🥛',
    convert: (qty, unit) => {
      const ml = toMl(qty, unit);
      if (ml == null) return null;
      const lts = ceil(ml / 1000);
      return { count: lts, label: `${lts} ${lts === 1 ? 'litro' : 'litros'} de leche` };
    },
  },
  {
    aliases: ['queso panela', 'queso fresco', 'queso oaxaca', 'queso'],
    emoji: '🧀',
    convert: (qty, unit) => {
      const g = toGrams(qty, unit);
      if (g == null) return null;
      const pkgs = ceil(g / 400);
      return { count: pkgs, label: `${pkgs} ${pkgs === 1 ? 'paquete' : 'paquetes'} de queso (400g c/u)` };
    },
  },

  // ─── Grasas / aceites ───
  {
    aliases: ['aceite de oliva', 'aceite oliva', 'aceite'],
    emoji: '🫒',
    convert: () => ({ count: 1, label: '1 botella de aceite de oliva' }),
  },
  {
    aliases: ['mantequilla'],
    emoji: '🧈',
    convert: () => ({ count: 1, label: '1 barra de mantequilla' }),
  },
  {
    aliases: ['nuez', 'nueces', 'almendras', 'almendra', 'nuez o almendras'],
    emoji: '🥜',
    convert: (qty, unit) => {
      const g = toGrams(qty, unit);
      if (g == null) return null;
      const bags = ceil(g / 250);
      return { count: bags, label: `${bags} ${bags === 1 ? 'bolsa' : 'bolsas'} de nueces (250g c/u)` };
    },
  },

  // ─── Granos / leguminosas ───
  {
    aliases: ['frijol', 'frijoles'],
    emoji: '🫘',
    convert: () => ({ count: 1, label: '1 bolsa de frijol (1 kg)' }),
  },
  {
    aliases: ['lenteja', 'lentejas'],
    emoji: '🫘',
    convert: () => ({ count: 1, label: '1 bolsa de lentejas (500g)' }),
  },
];

/** Encuentra el entry que matchea el ingrediente. Match por substring del
 *  nombre normalizado. */
function findEntry(ingredient: string): IngredientEntry | null {
  const ing = normalize(ingredient);
  for (const entry of INGREDIENT_CATALOG) {
    for (const alias of entry.aliases) {
      const a = normalize(alias);
      if (ing === a || ing.includes(a) || a.includes(ing)) {
        return entry;
      }
    }
  }
  return null;
}

/** Convierte un ítem (ingredient + qty + unit) a recomendación de compra.
 *  Si no hay match en el catálogo, devuelve null y la UI usa el formato
 *  original (gramos / tazas).
 *
 *  `bufferPieces` solo aplica si la entry está marcada como `isFreshProduce`.
 *  Para enlatados/empaquetados/granos se ignora (su tamaño de envase ya
 *  proporciona margen). */
export function recommendPurchase(
  ingredient: string,
  qty: number,
  unit: string,
  opts: ConvertOpts = {},
): PurchaseRecommendation | null {
  const entry = findEntry(ingredient);
  if (!entry) return null;
  const effectiveOpts: ConvertOpts = entry.isFreshProduce ? opts : {};
  const rec = entry.convert(qty, unit, effectiveOpts);
  if (!rec) return null;
  return { ...rec, emoji: rec.emoji ?? entry.emoji };
}
